// context/SupervisorContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { FLOOR_MANAGER_TOKEN_KEY} from '../constants/tokenKeys';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const FloorManagerContext = createContext(undefined);


const getStoredAuthHeaders = () => {
 const t = localStorage.getItem(
  window.location.pathname.startsWith("/lkart/superadmin/assign") ? "superadmintoken" : "auth_token"
);
  console.log("supervisor_token at call time:", t);
  return {
    Authorization: `Bearer ${t}`,
    "Content-Type": "application/json",
  };
};



export const FloorManagerProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [isHOD, setIsHOD] = useState(false);

  const getAuthHeaders = useCallback(() => ({
    Authorization: `Bearer ${token || localStorage.getItem(FLOOR_MANAGER_TOKEN_KEY)}`,
    "Content-Type": "application/json",
  }), [token]);

  const clearSession = useCallback(() => {
    localStorage.removeItem(FLOOR_MANAGER_TOKEN_KEY);
    localStorage.removeItem("department");
    localStorage.removeItem("departmentId");
    localStorage.removeItem("companyId");
    setToken(null);
    setUser(null);
    setIsHOD(false);
  }, []);

  const verifyToken = useCallback(async (tokenToVerify) => {
    try {
      const response = await axios.get(`${BASE_URL}/lenskart/verify-token`, {
        headers: { Authorization: `Bearer ${tokenToVerify}` },
      });

      if (response.data?.error?.name === "TokenExpiredError") {
        clearSession();
        return false;
      }

      if (response.data?.success && response.data?.user) {
        const u = response.data.user;
        localStorage.setItem("department", u.department);
        localStorage.setItem("departmentId", u.departmentId);
        localStorage.setItem("companyId", u.MyCompany);
        setIsHOD(u.isHOD === true);
        setUser({
          id: u.id,
          phone: u.phone,
          name: u.name,
          role: u.role,
          department: u.department,
          departmentId: u.departmentId,
          companyId: u.MyCompany,
          isHOD: u.isHOD === true,
        });
      }

      return true;
    } catch (error) {
      console.error("Token verification failed:", error);
      clearSession();
      return false;
    }
  }, [clearSession]);

  useEffect(() => {
    const restoreSession = async () => {
      try {
        const storedToken = localStorage.getItem(FLOOR_MANAGER_TOKEN_KEY);
        if (storedToken) {
          setToken(storedToken);
          await verifyToken(storedToken);
        }
      } catch (error) {
        console.error("Session restore failed:", error);
        clearSession();
      } finally {
        setLoading(false);
        setInitialized(true);
      }
    };
    restoreSession();
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      setLoading(true);
      const response = await axios.post(`${BASE_URL}/floor-admin/login`, {
        username: email,
        password,
      });

      if (!response.data.success || !response.data.token || !response.data.user) {
        throw new Error(response.data?.message || response.data?.error || "Invalid credentials");
      }

      const { token: newToken, user: u } = response.data;
      const isHOD = u.isHOD === true || u.role === "hod";

      localStorage.setItem(FLOOR_MANAGER_TOKEN_KEY, newToken);
      localStorage.setItem("department", u.department || "");
      localStorage.setItem("departmentId", u.departmentId || "");
      localStorage.setItem("companyId", u.companyId || "");
      localStorage.setItem("isHOD", isHOD);

      setToken(newToken);
      setIsHOD(isHOD);
      setUser({
        id: u.id,
        phone: u.phone,
        name: u.name,
        role: u.role,
        department: u.department,
        departmentId: u.departmentId,
        companyId: u.companyId,
        isHOD,
      });

      return { isHOD };
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Requirement History ────────────────────────────────────────────────────

  const fetchHistory = useCallback(async () => {
    const companyId = localStorage.getItem("companyId");
    const response = await axios.get(`${BASE_URL}/floor-admin`, {
      headers: getAuthHeaders(),
      params: { companyId },
    });
    return response.data;
  }, [getAuthHeaders]);

  // ── Add Requirement ────────────────────────────────────────────────────────

  const addRequirement = useCallback(async (requestData) => {
    const companyId = localStorage.getItem("companyId");
    const response = await axios.post(
      `${BASE_URL}/floor-admin/add`,
      { ...requestData, companyId },
      { headers: getAuthHeaders() }
    );
    return response.data;
  }, [getAuthHeaders]);

  // ── Update Requirement ─────────────────────────────────────────────────────

  const updateRequirement = useCallback(async (payload) => {
    const companyId = localStorage.getItem("companyId");
    const response = await axios.put(
      `${BASE_URL}/floor-admin/update`,
      { ...payload, companyId },
      { headers: getAuthHeaders() }
    );
    return response.data;
  }, [getAuthHeaders]);

  // ── Delete Requirement ─────────────────────────────────────────────────────

  const deleteRequirement = useCallback(async ({ date, slot }) => {
    const companyId = localStorage.getItem("companyId");
    const response = await axios.delete(`${BASE_URL}/floor-admin/delete`, {
      headers: getAuthHeaders(),
      data: { date, slot, companyId },
    });
    return response.data;
  }, [getAuthHeaders]);

  const hodAction = useCallback(async (requestId, action, reason = "") => {
    const payload = { requestId, action };
    if (action === "reject" && reason) payload.reason = reason;
    const response = await axios.post(
      `${BASE_URL}/floor-admin/hod-action`,
      payload,
      { headers: getAuthHeaders() }
    );
    return response.data;
  }, [getAuthHeaders]);

  // ── Time Slots ─────────────────────────────────────────────────────────────

  const fetchTimeSlots = useCallback(async (department, departmentId) => {
    const response = await fetch(`${BASE_URL}/lenskart/department-dropdown`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ department, departmentId }),
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
  }, []);

  const logout = clearSession;

  const value = {
    token,
    user,
    loading,
    initialized,
    isHOD,
    isLoggedIn: !!token,
    login,
    verifyToken,
    clearSession,
    logout,
    fetchHistory,
    addRequirement,
    updateRequirement,
    deleteRequirement,
    hodAction,
    fetchTimeSlots,

  };

  // if (!initialized) {
  //   return (
  //     <div className="min-h-screen flex items-center justify-center bg-gray-50">
  //       <p className="text-gray-500 text-sm">Loading...</p>
  //     </div>
  //   );
  // }

  return (
    <FloorManagerContext.Provider value={value}>
      {children}
    </FloorManagerContext.Provider>
  );
};

export const useFloorManager = () => {
  const ctx = useContext(FloorManagerContext);
  if (!ctx) throw new Error("useFloorManager must be used inside <FloorManagerProvider>");
  return ctx;
};